import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowsClockwise, Trash, CheckCircle } from '@phosphor-icons/react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BackupRestoreLeftovers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/admin/backup/restore-leftovers`);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao procurar restos de testes de restauro');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeAll = async () => {
    setConfirmOpen(false);
    setDeleting(true);
    try {
      const { data: r } = await axios.post(`${API}/api/admin/backup/restore-leftovers/delete`);
      setReport(r);
      toast.success('Limpeza concluída');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao apagar os restos');
    } finally {
      setDeleting(false);
    }
  };

  const groups = data ? [
    ...(data.app_collections?.collections ? [{ name: 'Coleções restore_test_* na base da app', ...data.app_collections }] : []),
    ...(data.databases || []).map((d) => ({ name: d.name, ...d })),
  ] : [];

  return (
    <div className="bg-white rounded-2xl border border-brand-border overflow-hidden" data-testid="restore-leftovers-section">
      <div className="px-4 py-3 border-b border-brand-border flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm font-medium text-brand-text">Restos de testes de restauro</span>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading || deleting}
            data-testid="restore-leftovers-refresh-btn"
            className="border border-brand-border rounded-full px-3 py-1 text-xs font-medium text-brand-text flex items-center gap-1.5 hover:bg-brand-bg disabled:opacity-50"
          >
            <ArrowsClockwise size={13} className={loading ? 'animate-spin' : ''} /> Procurar de novo
          </button>
          {data?.total_collections > 0 && (
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={deleting}
              data-testid="restore-leftovers-delete-btn"
              className="bg-red-600 text-white rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash size={13} /> {deleting ? 'A apagar…' : 'Apagar restos'}
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3 text-sm">
        {loading && !data && <p className="text-brand-muted">A procurar…</p>}

        {data && groups.length === 0 && (
          <p className="text-emerald-700 flex items-center gap-1.5" data-testid="restore-leftovers-empty">
            <CheckCircle size={16} /> Sem restos de testes
          </p>
        )}

        {groups.map((g) => (
          <div key={g.name} className="border border-brand-border rounded-xl p-3" data-testid={`restore-leftover-group-${g.name}`}>
            <p className="font-medium text-brand-text break-all">{g.name}</p>
            <p className="text-xs text-brand-muted mt-0.5">{g.collections} coleções · {g.documents} documentos</p>
            <p className="text-xs text-brand-muted mt-1 break-all">
              {g.items.slice(0, 8).map((i) => `${i.name} (${i.documents})`).join(', ')}{g.items.length > 8 ? ` … +${g.items.length - 8}` : ''}
            </p>
          </div>
        ))}

        {data?.errors?.length > 0 && (
          <ul className="text-xs text-amber-700 list-disc list-inside" data-testid="restore-leftovers-notes">
            {data.errors.slice(0, 4).map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        {report && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 space-y-1" data-testid="restore-leftovers-report">
            <p className="font-medium">Apagado: {report.total_deleted_collections} coleções · {report.all_empty ? 'ficou vazio' : 'ainda restam coleções'}</p>
            {report.deleted.map((r) => (
              <p key={r.name} className="break-all">{r.name}: {r.deleted_collections}/{r.found_collections} apagadas · {r.empty ? 'vazio' : 'não ficou vazio'}</p>
            ))}
            {report.deleted.length === 0 && <p>Não havia nada para apagar.</p>}
            {report.errors?.length > 0 && <p className="text-red-700">Erros: {report.errors.slice(0, 3).join('; ')}</p>}
            <p className="text-emerald-700">Ação registada no registo de acessos.</p>
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="restore-leftovers-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tem a certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Vão ser apagadas, coleção a coleção, {data?.total_collections || 0} coleções temporárias de testes de restauro
              ({data?.total_documents || 0} documentos). As coleções reais não são tocadas. Esta ação fica registada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-leftovers-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={removeAll} data-testid="restore-leftovers-confirm-btn" className="bg-red-600 hover:bg-red-700">
              Sim, apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
